import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ChevronDown, Users, Search, Compass, Building, Shield, Megaphone } from 'lucide-react';
import { services } from '../../data/mock';

gsap.registerPlugin(ScrollTrigger);

const iconMap = {
  users: Users,
  search: Search,
  compass: Compass,
  building: Building,
  shield: Shield,
  megaphone: Megaphone
};

const ServicesSection = () => {
  const sectionRef = useRef(null);
  const [expandedId, setExpandedId] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        '.service-title',
        { y: 60, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 1,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 75%',
            toggleActions: 'play none none reverse'
          }
        }
      );

      gsap.fromTo(
        '.service-item',
        { y: 40, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.6,
          stagger: 0.1,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: '.services-container',
            start: 'top 80%',
            toggleActions: 'play none none reverse'
          }
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  // Get the hovered service or first one if none hovered
  const activeService = hoveredId 
    ? services.find(s => s.id === hoveredId) 
    : services[0];

  return (
    <section
      id="servicios"
      ref={sectionRef}
      className="relative py-16 lg:py-28 bg-[#0a0a0a] overflow-hidden"
      data-testid="services-section"
    >
      {/* Background accent */}
      <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-white/[0.01] to-transparent" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="service-title mb-12 lg:mb-16">
          <span className="inline-block text-white/50 text-xs font-light tracking-[0.3em] uppercase mb-6">
            Nuestros Servicios
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-white">
            ¿A qué nos{' '}
            <span className="text-white/50 font-medium">dedicamos?</span>
          </h2>
        </div>

        {/* Services Container */}
        <div className="services-container">
          {/* Mobile: Accordion Style */}
          <div className="lg:hidden space-y-3">
            {services.map((service) => {
              const IconComponent = iconMap[service.icon] || Users;
              const isExpanded = expandedId === service.id;
              
              return (
                <div
                  key={service.id}
                  className="service-item"
                >
                  <button
                    onClick={() => toggleExpand(service.id)}
                    className={`w-full flex items-center justify-between p-5 rounded-xl border transition-all duration-300 ${
                      isExpanded 
                        ? 'bg-white/10 border-white/20' 
                        : 'bg-white/5 border-white/10'
                    }`}
                    data-testid={`service-accordion-${service.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                        isExpanded ? 'bg-white text-black' : 'bg-white/10 text-white'
                      }`}>
                        <IconComponent size={18} />
                      </div>
                      <span className="text-white font-light text-left">{service.title}</span>
                    </div>
                    <ChevronDown 
                      size={20} 
                      className={`text-white/50 transition-transform duration-300 ${
                        isExpanded ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  
                  {/* Expandable content */}
                  <div className={`overflow-hidden transition-all duration-300 ${
                    isExpanded ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'
                  }`}>
                    <p className="px-5 py-4 text-white/50 text-sm font-light leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop: Two Column Layout with Fixed Height */}
          <div className="hidden lg:grid lg:grid-cols-2 gap-12">
            {/* Left: Service List */}
            <div className="space-y-0">
              {services.map((service, index) => {
                const IconComponent = iconMap[service.icon] || Users;
                const isHovered = hoveredId === service.id;
                const isActive = hoveredId ? isHovered : index === 0;
                
                return (
                  <div
                    key={service.id}
                    className="service-item group cursor-pointer"
                    onMouseEnter={() => setHoveredId(service.id)}
                    data-testid={`service-item-${service.id}`}
                  >
                    <div className={`flex items-center gap-5 py-5 border-b transition-all duration-300 ${
                      isActive ? 'border-white/30' : 'border-white/10'
                    }`}>
                      {/* Number */}
                      <span className={`text-sm font-light w-6 transition-colors duration-300 ${
                        isActive ? 'text-white' : 'text-white/20'
                      }`}>
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      
                      {/* Icon */}
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                        isActive ? 'bg-white text-black' : 'bg-white/10 text-white/50'
                      }`}>
                        <IconComponent size={16} />
                      </div>
                      
                      {/* Title */}
                      <h3 className={`flex-1 text-lg font-light transition-colors duration-300 ${
                        isActive ? 'text-white' : 'text-white/50'
                      }`}>
                        {service.title}
                      </h3>

                      {/* Arrow */}
                      <svg
                        className={`w-5 h-5 transition-all duration-300 ${
                          isActive ? 'text-white translate-x-1' : 'text-white/20'
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Right: Service Description - Fixed Height */}
            <div className="flex items-center">
              <div className="bg-white/5 rounded-2xl p-8 border border-white/10 h-[300px] flex flex-col justify-center">
                <div className="mb-4">
                  {(() => {
                    const IconComponent = iconMap[activeService?.icon] || Users;
                    return (
                      <div className="w-14 h-14 rounded-full bg-white text-black flex items-center justify-center mb-6">
                        <IconComponent size={24} />
                      </div>
                    );
                  })()}
                </div>
                <h4 className="text-2xl font-light text-white mb-4">
                  {activeService?.title}
                </h4>
                <p className="text-white/50 font-light leading-relaxed">
                  {activeService?.description}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
